import { RuleTableConfiguration } from "./RuleTableConfiguration";
import { IContext } from "../../../Common/IContext";
import { IDesignPanelConfig } from "../../../Common/IDesignPanelConfig";
import { RuleBuilderPage } from "../../RuleBuilderPage";
import { StateManager } from "../../../Common/StateManager";
import { RuleTableColumn } from "./Structure/RuleTableRuleColumn";
import { DialogProps } from "../../../Common/DialogProps";
import { ResultCell } from "../Common/ResultCell";
import { AutoBind } from "../../../Common/AutoBind";
import { DataUtilities } from "../../../Common/DataUtilities";
import type { AppGlueAny } from "../../../Common/AppGlueAny";

export class RuleTableEditContext implements IContext {
    designConfig?: IDesignPanelConfig | undefined;
    runtimeConfig?: IDesignPanelConfig | undefined;
    clipboardElement: AppGlueAny;
    lastSelectionElement: AppGlueAny;

    mode: RuleBuilderPage = RuleBuilderPage.Rule;
    title?: string;
    definition: RuleTableConfiguration;
    public onRuleSave?: () => void;
    public onRuleCancel?: () => void;

    constructor(def: RuleTableConfiguration) {
        this.definition = def;
    }

    private _selectedColumnHeader: RuleTableColumn | undefined;

    get selectedColumnHeader() {
        return this._selectedColumnHeader;
    }

    set selectedColumnHeader(col: RuleTableColumn | undefined) {
        this._selectedColumnHeader = col;
        StateManager.propertyChanged(this, "selectedColumnHeader");
    }

    _notification: DialogProps | undefined;

    get notification() {
        return this._notification;
    }

    set notification(notification: DialogProps | undefined) {
        this._notification = notification;
        StateManager.propertyChanged(this, "notification");
    }

    private _selectedCell: ResultCell | undefined;

    get selectedCell() {
        return this._selectedCell;
    }

    set selectedCell(cell: ResultCell | undefined) {
        this._selectedCell = cell;
        StateManager.propertyChanged(this, "selectedCell");
    }

    private _deletingColumn: RuleTableColumn | undefined;

    get deletingColumn(): RuleTableColumn | undefined {
        return this._deletingColumn;
    }

    set deletingColumn(column: RuleTableColumn | undefined) {
        this._deletingColumn = column;
        StateManager.propertyChanged(this, "deletingColumn");
    }

    @AutoBind
    onSave() {
        if (this.definition && this.title) {
            this.definition.title = this.title;
        }

        if (this.onRuleSave) {
            this.onRuleSave();
        }
    }

    @AutoBind
    onCancel() {
        this.title = this.definition?.title || "";
        if (this.onRuleCancel) {
            this.onRuleCancel();
        }
    }

    // eslint-disable-next-line
    onCopy(id?: string) {}

    // eslint-disable-next-line
    onCut(id?: string) {}

    // eslint-disable-next-line
    onDelete(id?: string) {}

    // eslint-disable-next-line
    onPaste(id?: string) {}

    navigate = (direction: "Up" | "Down" | "Right" | "Left" | "Tab" | "Select"): void => {
        if (!this.selectedCell) return;

        let newCell: ResultCell | null = null;

        if (direction === "Left") {
            newCell = this.definition.getPrevCell(this.selectedCell);
        } else if (direction === "Right" || direction === "Tab") {
            newCell = this.definition.getNextCell(this.selectedCell);
        } else if (direction === "Down") {
            newCell = this.definition.getNextRowCell(this.selectedCell);
        } else if (direction === "Up") {
            newCell = this.definition.getPrevRowCell(this.selectedCell);
        }
        if (newCell) {
            this.selectedCell = newCell;
        }
    };

    isSelected(cell: ResultCell): boolean {
        return cell === this.selectedCell;
    }

    setSelected(cell: ResultCell | undefined) {
        this.selectedCell = cell;
    }

    removeRuleColumn(column: RuleTableColumn) {
        if (
            this.definition.columnSafeDeletable(column._id) ||
            DataUtilities.getCookie("RULE_TABLE_DELETE_CONFIRM") === "true"
        ) {
            this.definition.removeRuleColumn(column._id);
            this.onDelete(column._id);
            StateManager.propertyChanged(this.definition, "ruleColumns");
        } else {
            this.deletingColumn = column;
        }
    }

    forceRemoveColumn(column: RuleTableColumn) {
        this.definition.removeRuleColumn(column._id);
        this.onDelete(column._id);
    }
}
